import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Redux + MUI + etc
import { ThemeProvider } from "@mui/material";
import { configureStore } from "@reduxjs/toolkit";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";

// === Stytch B2B imports ===

// Your own modules
import ProtectedRoute from "./authentication/ProtectedRoute";
import generateMuiThemeDefaults from "./functions/generateMuiThemeDefaults";
import "./index.css";
import rootReducer from "./redux/reducers";

// AG Grid
import { StytchProvider } from "@stytch/react";
import { StytchUIClient } from "@stytch/vanilla-js";
import { LicenseManager } from "ag-grid-enterprise";
import Authenticate from "./authentication/Authenticate";
import CreateAccountScreen from "./screens/CreateAccountScreen";

import { yellow } from "@mui/material/colors";
import AuthenticatedDashboard from "./AuthenticatedDashboard";
import DashboardHomeScreen from "./screens/DashboardHomeScreen";
import FileUploadScreen from "./screens/FileUploadScreen/FileUploadScreen";
import LoginScreen from "./screens/LoginScreen";
import NewFinancingRequestScreen from "./screens/NewFinancingRequestScreen";
import DrawRequestScreen from "./screens/NewFinancingRequestScreen/DrawRequestScreen";
import ProfileScreen from "./screens/ProfileScreen/ProfileScreen";
import OffMarketListingsScreen from "./screens/OffMarketListingsScreen/OffMarketListingsScreen";
import FinancingRequestsScreen from "./screens/NewFinancingRequestScreen/FinancingRequestsScreen";
import { useEffect } from "react";
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_KEY);

// Create Redux store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== "production",
});

const primaryColor = yellow[700];
// const primaryColor = "#fbca0b";
// const primaryColor = "#2962ff";
// MUI-themed wrapper
const stytchOptions = {
  cookieOptions: {
    opaqueTokenCookieName: "stytch_session",
    jwtCookieName: "stytch_session_jwt",
    path: "",
    availableToSubdomains: false,
    domain: "",
  },
};

const stytchClient = new StytchUIClient(
  process.env.REACT_APP_STYTCH_PUBLIC_LIVE_TOKEN,
  stytchOptions,
);

const ContentRenderer = () => {
  useEffect(() => {
    // Decide title + favicon based on env
    // let title = "LO Portal";
    let title = `${process.env.REACT_APP_PAGE_TITLE}`;
    let faviconPath = `${process.env.REACT_APP_FAVICON_PATH}`;
    // let faviconPath = "%PUBLIC_URL%/favicon.ico";
    // let faviconPath = "%PUBLIC_URL%/favicon-generic.ico";

    document.title = title;
    // document.title = process.env.REACT_APP_PAGE_TITLE || title;

    // 2) Set favicon
    const head = document.querySelector("head");
    if (!head) return;

    // Try to find existing <link rel="icon">, otherwise create it
    let faviconLink =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    faviconLink.rel = "icon";
    // CRA doesn't expand %PUBLIC_URL% at runtime, only at build-time,
    // so build the path with process.env.PUBLIC_URL.
    const publicUrl = process.env.PUBLIC_URL || "";
    faviconLink.href = faviconPath.replace("%PUBLIC_URL%", publicUrl);

    // If it was newly created, append it
    if (!faviconLink.parentNode) {
      head.appendChild(faviconLink);
    }
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route
        path="/sign-up"
        element={<CreateAccountScreen partner={"handsome-homebuyer"} />}
      />
      <Route path="/authenticate" element={<Authenticate />} />

      {/* Private/Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AuthenticatedDashboard>
              <Routes>
                <Route path={"/"} element={<DashboardHomeScreen />} />

                <Route path={"/profile/*"} element={<ProfileScreen />} />
                <Route
                  path={"/off-market-listings"}
                  element={<OffMarketListingsScreen />}
                />
                <Route
                  path="/financing-requests"
                  element={<FinancingRequestsScreen />}
                />
                <Route
                  path="/financing-request/create"
                  element={<NewFinancingRequestScreen />}
                />
                <Route path={"/files"} element={<FileUploadScreen />} />
                <Route path={"/file-upload"} element={<FileUploadScreen />} />
                <Route path={"/draw-request"} element={<DrawRequestScreen />} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </AuthenticatedDashboard>
          </ProtectedRoute>
        }
      />

      {/* Redirect unknown paths */}
      {/* <Route path="*" element={<Navigate to="/login" />} /> */}
    </Routes>
  );
};

// Mount the app
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <StytchProvider stytch={stytchClient}>
    <Provider store={store}>
      <BrowserRouter>
        <SnackbarProvider maxSnack={5}>
          <ThemeProvider theme={generateMuiThemeDefaults({ primaryColor })}>
            <ContentRenderer />
          </ThemeProvider>
        </SnackbarProvider>
      </BrowserRouter>
    </Provider>
  </StytchProvider>,
);
